import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import { CanDeactivateFn, Router} from '@angular/router';
import { AppPath } from '@app/app-path';
import { AppLocale } from '@core/models/app-locale';
import { OrderAdDetail } from '@core/models/order-ad-detail.model';
import { OrderService } from '@core/models/order-service';
import { Order } from '@core/models/order.model';
import { Service } from '@core/models/service';
import { ServicesResponse } from '@core/models/services-response';
import { AppLocaleService } from '@core/services/app-locale.service';
import { Logger } from '@core/services/logger.service';
import { ServiceService } from '@core/services/service.service';
import { TranslateService } from '@ngx-translate/core';
import { OrderNavigationState } from '@order/models/order-navigation-state';
import { OrderServicesNavigationState } from '@order/models/order-services-navigation-state';
import { PackageViewModel } from '@order/viewModels/package.viewModel';
import { ServiceViewModel } from '@order/viewModels/service.viewModel';
import { DateTimeService } from '@shared/services/date-time.service';
import { BehaviorSubject, forkJoin, map } from 'rxjs';
import { SubSink } from 'subsink';
import { CassError } from '../../core/models/cass-error';
import {AuthService} from '@core/services/auth.service';

export function externalPlannerDeactivateGuard(urlToCheck: string): CanDeactivateFn<any> {
	return (component, currentRoute, currentState, nextState) => {
		const authService = inject(AuthService)
		const hasPlanId = !!authService.getExternalPlannerInfo?.planId

		if (!nextState.url.includes(urlToCheck) && hasPlanId) {
			// Marathon Plan ID should be discarded is user route to any other page
			authService.clearExternalPlannerInfo();
		}
		return true;
	}
}

interface PackageToServiceResponse {
	packageId: string;
	servicesResponse: ServicesResponse;
}

@Component({
	selector: 'cass-order-service',
	templateUrl: './order-service.component.html',
	styleUrls: ['./order-service.component.css']
})
export class OrderServiceComponent implements OnInit, OnDestroy {

	private readonly COMPONENT_NAME = "OrderServiceComponent";
	private readonly MONTH_COUNT = 12;

	loading$ = new BehaviorSubject<boolean>(false);
	initErrorMessageKey: string | undefined;
	initErrorMessage: string | undefined;
	advertiserName: string | undefined;

	packagesVMs: PackageViewModel[] = [];
	servicesVMs: ServiceViewModel[] = [];
	availableDates: Date[] = [];

	selectedPackageId: string | undefined;
	selectedServiceId: string | undefined;
	selectedDates: Date[] = [];

	isReady: boolean = false;
	isModified: boolean = false;
	canApply: boolean = false;

	private navigationState: OrderServicesNavigationState | undefined;
	private order: Order | undefined;
	private packageMap = new Map<string, string>();
	private packagesToServiceResponses: PackageToServiceResponse[] = [];

	private _localeId = "en";

	private _subs = new SubSink();

	constructor (
		private _dateTimeService: DateTimeService,
		private _localeService: AppLocaleService,
		private _logger: Logger,
		private _router: Router,
		private _serviceService: ServiceService,
		private _translate: TranslateService
	) {
		const nav = this._router.getCurrentNavigation();
		if (nav && nav.extras && nav.extras.state) {
			this.navigationState = nav.extras.state as OrderServicesNavigationState;
		}
	}

	ngOnInit(): void {
		this.initComponent();
	}

	ngOnDestroy(): void {
		this._subs.unsubscribe();
	}

	private initComponent(): void {
		if (!this.navigationState) {
			this.initErrorMessageKey = "ERRORS.UNEXPECTED_CODE_ERROR";
			this._logger.error(this.COMPONENT_NAME, "The navigationState is not defined.");
			return;
		}

		if (!this.navigationState.order) {
			this.initErrorMessageKey = "ERRORS.UNEXPECTED_CODE_ERROR";
			this._logger.error(this.COMPONENT_NAME, "The order in the navigationState is not defined.");
			return;
		}

		if (!this.navigationState.order.orderAdDetails || this.navigationState.order.orderAdDetails.length === 0) {
			this.initErrorMessageKey = "ERRORS.UNEXPECTED_CODE_ERROR";
			this._logger.error(this.COMPONENT_NAME, "There are no order lines in the order passed via the navigationState.");
			return;
		}

		if (!this.navigationState.packageMap) {
			this.initErrorMessageKey = "ERRORS.UNEXPECTED_CODE_ERROR";
			this._logger.error(this.COMPONENT_NAME, "The packageMap passed via the navigationState is empty.");
			return;
		}

		this.order = this.navigationState.order;
		this.advertiserName = this.order?.orderHeader?.advertiserName;
		this.packageMap = this.navigationState.packageMap;

		const uniquePackagesIds = [...new Set(this.order.orderAdDetails.map((orderLine: OrderAdDetail) => orderLine.packageId))];

		const dateTimeNow = new Date();
		const startYear = dateTimeNow.getFullYear();
		const startMonth = dateTimeNow.getMonth() + 1;

		this._subs.add(
			this._localeService.currentLocale$.subscribe((locale: AppLocale | undefined) => {
				if (locale && locale.localeId && locale.localeId !== this._localeId) {
					this._localeId = locale.localeId;

					// re-sort the packages and services using new locale
					this.packagesVMs = this.sortPackages(this.packagesVMs);
					this.servicesVMs = this.sortServcies(this.servicesVMs);
				}
			})
		);

		this.loading$.next(true);

		this._subs.add(
			forkJoin(
				// for each unique package id, request services and dates
				uniquePackagesIds.map((packageId: string) => this._serviceService.getServices(packageId, startYear, startMonth, this.MONTH_COUNT).pipe(
					map((response: ServicesResponse) => ({ packageId: packageId, servicesResponse: response }))
				))
			).subscribe({
				next: (res) => {
					this.loading$.next(false);
					this.packagesToServiceResponses = res;
					this.displayPackages(this.packagesToServiceResponses);
				},
				error: (error: CassError) => {
					this.loading$.next(false);
					this._logger.error(this.COMPONENT_NAME, "Init error");

					const errorKey = error.errorMessageKey ?? "ERRORS.UNEXPECTED_API_ERROR";

					this._translate.get(errorKey, error.errorInterpolateParams).subscribe((msg) => {
						this._logger.error(this.COMPONENT_NAME, "Init error", msg);
						this.initErrorMessage = msg;
					});
				}
			}));
	}

	private displayPackages(packageToServiceResponse: PackageToServiceResponse[]): void {
		const packagesWithServices = packageToServiceResponse.filter((val) => val && val.servicesResponse && val.servicesResponse.services && val.servicesResponse.services.length > 0);
		if (packagesWithServices.length === 0) {
			// no packages with services
			this.initErrorMessageKey = "ORDER_BASKET.ORDER_SERVICES.ERROR_PACKAGES_DO_NOT_HAVE_SERVICES";
			return;
		}

		const packagesVMs = packagesWithServices.map((val) => {
			const packageId = val.packageId;
			const packageLabel = this.packageMap.get(packageId);

			if (!packageLabel) {
				this._logger.error(this.COMPONENT_NAME, `The packageId="${packageId}" has no label`);
			}

			return new PackageViewModel(packageId, packageLabel || packageId);
		});

		this.packagesVMs = this.sortPackages(packagesVMs);

		// auto-select a single package
		if (this.packagesVMs.length === 1) {
			this.selectedPackageId = this.packagesVMs[0].id;
			this.onPackageSelectionChange();
		}

		this.isReady = true;
	}

	private displayServicesAndDates(servicesResponse: ServicesResponse, packageId: string): void {

		const servicesVMs: ServiceViewModel[] = [];
		if (servicesResponse.services && servicesResponse.services.length > 0) {
			servicesResponse.services.forEach((service: Service) => {
				if (service.id && service.label) {
					const serviceViewModel = new ServiceViewModel(service.id, service.label);
					servicesVMs.push(serviceViewModel);
				}
				else {
					this._logger.warning(this.COMPONENT_NAME, `The list of services for packageId="${packageId}" contains an entry with empty id and label`);
				}
			});
		}

		this.servicesVMs = this.sortServcies(servicesVMs);

		// auto-select a single service
		if (this.servicesVMs.length === 1) {
			this.selectedServiceId = this.servicesVMs[0].id;
			this.onServiceSelectionChange();
		}

		if (servicesResponse.issueDates && servicesResponse.issueDates.length > 0) {
			this.availableDates = servicesResponse.issueDates.map((issueDate: string) => new Date(issueDate));
		}
		else {
			this.availableDates = [];
		}
	}

	onPackageSelectionChange(): void {
		this.clearServicesSelection();
		this.clearDatesSelection();

		if (this.selectedPackageId) {
			const packageToServicesInfo = this.packagesToServiceResponses.find((packageToServicesInfo: PackageToServiceResponse) => packageToServicesInfo.packageId === this.selectedPackageId);
			if (packageToServicesInfo) {
				this.displayServicesAndDates(packageToServicesInfo.servicesResponse, this.selectedPackageId);
			}
		}

		this.isModified = true;
		this.updateActions();
	}

	private clearServicesSelection(): void {
		this.selectedServiceId = undefined;
	}

	private clearDatesSelection(): void {
		this.selectedDates = [];
	}

	onServiceSelectionChange(): void {
		this.isModified = true;
		this.updateActions();
	}

	selectDates(issues: readonly Date[]): void {
		this.selectedDates = [...issues];

		this.isModified = true;
		this.updateActions();
	}

	private updateActions(): void {
		this.canApply = Boolean(this.selectedPackageId && this.selectedServiceId && this.selectedDates.length > 0);
	}

	applyAction(): void {
		// pre-processing verifications
		if (!this.selectedPackageId) {
			this._logger.error(this.COMPONENT_NAME, "Apply action is called when the package is not selected.");
			return;
		}

		if (!this.selectedServiceId) {
			this._logger.error(this.COMPONENT_NAME, "Apply action is called when the serice is not selected.");
			return;
		}

		if (this.selectedDates.length === 0) {
			this._logger.error(this.COMPONENT_NAME, "Apply action is called when the dates are not selected.");
			return;
		}

		if (!this.order) {
			this._logger.error(this.COMPONENT_NAME, "Apply action is called but the order is empty.");
			return;
		}

		// create order service lines and add them to the order
		if (!this.order.orderServices) {
			this.order.orderServices = [];
		}

		for (let i = 0; i < this.selectedDates.length; ++i) {
			const selectedDate = this.selectedDates[i];
			const orderServiceLine = new OrderService();
			orderServiceLine.packageId = this.selectedPackageId;
			orderServiceLine.serviceId = this.selectedServiceId;
			orderServiceLine.issueDate = this._dateTimeService.toBackendDate(selectedDate);

			this.order.orderServices.push(orderServiceLine);
		}

		// send updated order to the Order Component
		const state = new OrderNavigationState();
		state.order = this.order;
		state.needsCalc = true;
		state.isUpdated = true;

		this._router.navigate([AppPath.Order], { state: state });
	}

	cancelAction(): void {
		const state = new OrderNavigationState();
		state.order = this.order;
		state.needsCalc = false;
		state.isUpdated = this.navigationState?.orderHasUnsavedChanges || false; // keep the original flag as the order could be chaged before

		this._router.navigate([AppPath.Order], { state: state });
	}

	private sortPackages(packagesVMs: PackageViewModel[]): PackageViewModel[] {
		const vms = [...packagesVMs];
		return vms.sort((a: PackageViewModel, b: PackageViewModel) => a.label.localeCompare(b.label, this._localeId));
	}

	private sortServcies(servicesVMs: ServiceViewModel[]): ServiceViewModel[] {
		const vms = [...servicesVMs];
		return vms.sort((a: ServiceViewModel, b: ServiceViewModel) => a.label.localeCompare(b.label, this._localeId));
	}
}
